import React, { useEffect, useRef } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ShoppingBagIcon, XIcon } from 'lucide-react';
import { useNavDrawer } from '../../contexts/NavDrawerContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useCart } from '../../contexts/CartContext';
import { drawerNav } from '../../data/navigation';
import { Wordmark } from '../brand/Wordmark';
import { LangSwitch } from './LangSwitch';
import { ShopNowButton } from '../shop/ShopNowButton';
const EASE = [0.23, 1, 0.32, 1] as const;
export function NavDrawer() {
  const {
    isOpen,
    close,
    triggerRef
  } = useNavDrawer();
  const {
    t,
    lang
  } = useLanguage();
  const {
    totals
  } = useCart();
  const {
    pathname
  } = useLocation();
  const reduce = useReducedMotion();
  const panelRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  // Close when the route changes.
  useEffect(() => {
    close();
  }, [pathname, close]);

  // Focus management, Escape, focus trap, scroll lock.
  useEffect(() => {
    if (!isOpen) return;
    const previous = document.activeElement as HTMLElement | null;
    const raf = requestAnimationFrame(() => closeRef.current?.focus());
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        close();
        return;
      }
      if (e.key !== 'Tab' || !panelRef.current) return;
      const focusables = Array.from(panelRef.current.querySelectorAll<HTMLElement>('a[href], button:not([disabled])'));
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      // Restore focus to the trigger only if focus hasn't intentionally moved elsewhere
      // (e.g. SHOP NOW moved it to the shop section).
      const active = document.activeElement;
      const focusLeftDrawer = active && active !== document.body && !panelRef.current?.contains(active);
      if (!focusLeftDrawer) {
        const target = triggerRef.current ?? previous;
        if (target && document.contains(target)) target.focus({
          preventScroll: true
        });
      }
    };
  }, [isOpen, close, triggerRef]);
  return <AnimatePresence data-id="element-459" data-mp="3uo16y9hbqplu">
      {isOpen && <div className="fixed inset-0 z-[60]" data-id="element-460" data-mp="3jkldc4tgor7">
          <motion.div className="absolute inset-0 bg-[#030A14]/70 backdrop-blur-sm" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} onClick={close} aria-hidden data-id="element-461" data-mp="3km668bmwpm4" />
          <motion.div ref={panelRef} id="site-nav-drawer" role="dialog" aria-modal="true" aria-label={lang === 'es' ? 'Navegación del sitio' : 'Site navigation'} className="absolute inset-y-0 right-0 flex w-full max-w-[440px] flex-col border-l border-marine/25 bg-navy shadow-2xl" initial={reduce ? {
        opacity: 0
      } : {
        x: '100%'
      }} animate={reduce ? {
        opacity: 1
      } : {
        x: 0
      }} exit={reduce ? {
        opacity: 0
      } : {
        x: '100%'
      }} transition={{
        duration: 0.28,
        ease: EASE
      }} data-id="element-462" data-mp="4y445nn4rs6y">
            <div className="flex h-[72px] items-center justify-between border-b mf-hairline px-6" data-id="element-463" data-mp="2pocpml81afyd">
              <Wordmark data-id="element-464" data-mp="2kp9vvwxi7w5p" />
              <button ref={closeRef} type="button" onClick={close} aria-label={lang === 'es' ? 'Cerrar menú' : 'Close menu'} className="flex h-11 w-11 items-center justify-center rounded-md text-titanium transition-colors duration-150 hover:bg-white/10 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-ice" data-id="element-465" data-mp="2yebjzlj15u41">
                <XIcon className="h-6 w-6" data-id="element-466" data-mp="3h3rlab2jhqli" />
              </button>
            </div>

            <nav aria-label={lang === 'es' ? 'Principal' : 'Main'} className="flex-1 overflow-y-auto px-6 py-6" data-id="element-467" data-mp="3m2urvzy644vl">
              <ul data-id="element-468" data-mp="3r42183fylc4d">
                {drawerNav.map((item, i) => <motion.li key={item.to} initial={reduce ? false : {
              opacity: 0,
              x: 16
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              duration: 0.22,
              delay: reduce ? 0 : 0.06 + i * 0.035,
              ease: EASE
            }} data-id="element-469" data-mp="3w2d9wauipgl0" data-mpi={i}>
                    <NavLink to={item.to} end={item.to === '/'} onClick={close} className={({
                isActive
              }) => `group flex items-center gap-4 border-b mf-hairline py-4 font-display text-[30px] font-semibold uppercase leading-none tracking-[0.05em] transition-colors duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ice ${isActive ? 'text-marine' : 'text-white hover:text-ice'}`} data-id="element-470" data-mp="it7qzpgqijpv">
                      {({
                  isActive
                }) => <>
                          <span aria-hidden className={`h-6 w-[3px] rounded-full ${isActive ? 'bg-marine' : 'bg-transparent'}`} data-id="element-471" data-mp="nsanglinfhm8" />
                          <span aria-current={isActive ? 'page' : undefined} data-id="element-472" data-mp="38hqkvzcdi4ku">{t(item.key)}</span>
                        </>}
                    </NavLink>
                  </motion.li>)}
              </ul>
            </nav>

            <div className="space-y-5 border-t mf-hairline px-6 py-6" data-id="element-473" data-mp="3evw57n15kztk">
              <ShopNowButton source="drawer" showPrice onActivate={close} className="w-full" data-id="element-474" data-mp="30xz5wivbefb7" />
              <Link to="/order-tracking" onClick={close} className="block text-sm text-titanium hover:text-white" data-id="element-475" data-mp="35w0oz3sqqemo">
                {t('nav.track')}
              </Link>
              <div className="flex items-center justify-between" data-id="element-476" data-mp="2vznxmcko4cv3">
                <LangSwitch data-id="element-477" data-mp="2i7zngetzbygr" />
                <Link to="/cart" onClick={close} className="inline-flex items-center gap-2 text-sm text-titanium hover:text-white" data-id="element-478" data-mp="2i8sm0sisbhnt">
                  <ShoppingBagIcon className="h-4 w-4" data-id="element-479" data-mp="289jv68j7uj5o" />
                  {lang === 'es' ? 'Carrito' : 'Cart'} ({totals.lights})
                </Link>
              </div>
            </div>
          </motion.div>
        </div>}
    </AnimatePresence>;
}